import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { PromotionsAdminAPI } from "../../api/promotions";
import type {
  MarketingEmailCaptureFilters,
  MarketingEmailCaptureListResponse,
} from "../../types/promotions";

type EmailCaptureFilterState = {
  search: string;
  status: string;
  couponCode: string;
  registeredFrom: string;
  registeredTo: string;
  converted: string;
  emailSent: string;
  minOrders: string;
  maxOrders: string;
  minSpend: string;
  maxSpend: string;
};

type EmailTemplateKey = "promo_update" | "new_arrivals" | "review_reminder";

type EmailComposerState = {
  mode: "selected" | "filter";
  template: EmailTemplateKey;
  subject: string;
  message: string;
};

const EMAIL_CAPTURE_LAST_SEEN_KEY = "admin_marketing_email_capture_last_seen_at";
const EMAIL_CAPTURE_SEEN_EVENT = "admin-email-captures-seen";

const panelStyle: CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 0,
  background: "#fff",
  padding: 18,
};

const labelStyle: CSSProperties = {
  display: "grid",
  gap: 5,
};

const hintStyle: CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: "#64748b",
};

const defaultFilters: EmailCaptureFilterState = {
  search: "",
  status: "",
  couponCode: "",
  registeredFrom: "",
  registeredTo: "",
  converted: "",
  emailSent: "",
  minOrders: "",
  maxOrders: "",
  minSpend: "",
  maxSpend: "",
};

const defaultComposer: EmailComposerState = {
  mode: "selected",
  template: "promo_update",
  subject: "",
  message: "",
};

const EMAIL_TEMPLATES: Array<{
  key: EmailTemplateKey;
  name: string;
  eyebrow: string;
  subject: string;
  message: string;
  accent: string;
  soft: string;
  cta: string;
}> = [
  {
    key: "promo_update",
    name: "Welcome Offer",
    eyebrow: "Offer follow-up",
    subject: "Your Beyond Pendants offer is ready",
    message:
      "Hi,\n\nWe saved your Beyond Pendants discount for your first order. Pick your favorite pendant, add your photo, and use your promo code at checkout before it expires.",
    accent: "#059669",
    soft: "#ecfdf5",
    cta: "Shop Beyond Pendants",
  },
  {
    key: "new_arrivals",
    name: "New Arrivals",
    eyebrow: "Product story",
    subject: "New pendant styles just landed",
    message:
      "Hi,\n\nWe added new pendant options and photo styles that make it easier to build a gift with front and back photos. Take a quick look when you are ready.",
    accent: "#2563eb",
    soft: "#eff6ff",
    cta: "Shop new arrivals",
  },
  {
    key: "review_reminder",
    name: "Review Thank-You",
    eyebrow: "Review reward",
    subject: "A small thank-you from Beyond Pendants",
    message:
      "Hi,\n\nThank you for checking out Beyond Pendants. When you order and review us, you can unlock your welcome discount for your next pendant or gift.",
    accent: "#7c3aed",
    soft: "#f5f3ff",
    cta: "Visit Beyond Pendants",
  },
];

const money = (value: number | undefined) =>
  `$${Math.max(0, Number(value || 0)).toFixed(2)}`;

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const cleanFilters = (
  filters: EmailCaptureFilterState,
): MarketingEmailCaptureFilters => {
  const next: MarketingEmailCaptureFilters = {};
  Object.entries(filters).forEach(([key, value]) => {
    const trimmed = String(value || "").trim();
    if (trimmed) next[key as keyof MarketingEmailCaptureFilters] = trimmed;
  });
  return next;
};

function TemplatePreview({
  template,
  selected,
  onSelect,
}: {
  template: (typeof EMAIL_TEMPLATES)[number];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        border: `1px solid ${selected ? template.accent : "#e2e8f0"}`,
        background: selected ? template.soft : "#fff",
        borderRadius: 0,
        padding: 0,
        textAlign: "left",
        cursor: "pointer",
        overflow: "hidden",
      }}
    >
      <div style={{ background: template.soft, padding: 10 }}>
        <div
          style={{
            color: template.accent,
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: ".12em",
            textTransform: "uppercase",
          }}
        >
          {template.eyebrow}
        </div>
        <div style={{ marginTop: 5, fontWeight: 900, color: "#0f172a" }}>
          {template.name}
        </div>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
          {template.subject}
        </div>
        <div style={{ marginTop: 8, color: "#64748b", fontSize: 12, lineHeight: 1.45 }}>
          {template.message.replace(/\s+/g, " ").slice(0, 118)}...
        </div>
        <span
          style={{
            display: "inline-block",
            marginTop: 10,
            background: template.accent,
            color: "#fff",
            padding: "6px 8px",
            fontSize: 11,
            fontWeight: 900,
          }}
        >
          {template.cta}
        </span>
      </div>
    </button>
  );
}

export default function EmailLeadsPage() {
  const [filters, setFilters] = useState<EmailCaptureFilterState>(defaultFilters);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState<MarketingEmailCaptureListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [composer, setComposer] = useState<EmailComposerState>(defaultComposer);
  const [sending, setSending] = useState(false);

  const visibleRows = data?.items || [];
  const selectedIdsList = useMemo(
    () =>
      Object.entries(selectedIds)
        .filter(([, selected]) => selected)
        .map(([id]) => id),
    [selectedIds],
  );
  const selectedCount = selectedIdsList.length;
  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((item) => selectedIds[item._id]);
  const selectedTemplate =
    EMAIL_TEMPLATES.find((item) => item.key === composer.template) ||
    EMAIL_TEMPLATES[0];

  const refresh = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      setError(null);
      try {
        const result = await PromotionsAdminAPI.listEmailCaptures({
          ...cleanFilters(filters),
          limit,
        });
        setData(result);
        setSelectedIds((current) => {
          const visibleIds = new Set((result.items || []).map((item) => item._id));
          return Object.fromEntries(
            Object.entries(current).filter(([id]) => visibleIds.has(id)),
          );
        });

        const newestCreatedAt = result.summary?.newestCreatedAt || "";
        if (newestCreatedAt && typeof window !== "undefined") {
          const stored = window.localStorage.getItem(EMAIL_CAPTURE_LAST_SEEN_KEY) || "";
          if (!stored) {
            window.localStorage.setItem(EMAIL_CAPTURE_LAST_SEEN_KEY, newestCreatedAt);
            setNewLeadCount(0);
          } else {
            const storedMs = new Date(stored).getTime();
            setNewLeadCount(
              (result.items || []).filter((item) => {
                const createdMs = new Date(item.createdAt || "").getTime();
                return Number.isFinite(createdMs) && createdMs > storedMs;
              }).length,
            );
          }
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load email leads.",
        );
      } finally {
        if (!background) setLoading(false);
      }
    },
    [filters, limit],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refresh(true);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const toggleVisibleRows = () => {
    setSelectedIds((current) => {
      const next = { ...current };
      if (allVisibleSelected) {
        visibleRows.forEach((item) => delete next[item._id]);
        return next;
      }
      visibleRows.forEach((item) => {
        next[item._id] = true;
      });
      return next;
    });
  };

  const markSeen = () => {
    const newestCreatedAt = data?.summary?.newestCreatedAt || "";
    if (newestCreatedAt && typeof window !== "undefined") {
      window.localStorage.setItem(EMAIL_CAPTURE_LAST_SEEN_KEY, newestCreatedAt);
      window.dispatchEvent(new Event(EMAIL_CAPTURE_SEEN_EVENT));
    }
    setNewLeadCount(0);
  };

  const applyTemplate = () => {
    setComposer((prev) => ({
      ...prev,
      subject: selectedTemplate.subject,
      message: selectedTemplate.message,
    }));
  };

  const sendCampaign = async () => {
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const subject = composer.subject.trim();
      const message = composer.message.trim();
      if (!subject) throw new Error("Subject is required.");
      if (!message) throw new Error("Message is required.");
      if (composer.mode === "selected" && selectedCount <= 0) {
        throw new Error("Select at least one email or switch to matching filters.");
      }
      const result = await PromotionsAdminAPI.sendEmailCaptures({
        mode: composer.mode,
        template: composer.template,
        ids: composer.mode === "selected" ? selectedIdsList : undefined,
        filters: composer.mode === "filter" ? cleanFilters(filters) : undefined,
        subject,
        message,
      });
      const firstFailure = result.failed?.[0]?.error;
      if (result.sentCount <= 0 && result.failedCount > 0) {
        setError(
          firstFailure
            ? `No emails were sent. First error: ${firstFailure}`
            : `No emails were sent. ${result.failedCount} failed.`,
        );
      } else {
        setNotice(
          `Email sent to ${result.sentCount}. ${result.failedCount} failed, ${result.skippedCount} skipped.${
            firstFailure ? ` First error: ${firstFailure}` : ""
          }`,
        );
      }
      if (result.sentCount > 0) {
        setComposer(defaultComposer);
        setSelectedIds({});
      }
      await refresh(true);
    } catch (err: any) {
      const isTimeout =
        err?.code === "ECONNABORTED" ||
        String(err?.message || "").toLowerCase().includes("timeout");
      setError(
        isTimeout
          ? "Sending timed out. Check backend SMTP settings and try again."
          : err?.response?.data?.message ||
              err?.response?.data?.failed?.[0]?.error ||
              err?.message ||
              "Failed to send email campaign.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gap: 18,
        maxWidth: 1480,
        margin: "0 auto",
        width: "100%",
      }}
      className="email-leads-page"
    >
      <style>{`
        .email-leads-page input:not([type="checkbox"]),
        .email-leads-page select,
        .email-leads-page textarea {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 0;
          min-height: 38px;
          padding: 8px 10px;
          background: #fff;
          color: #0f172a;
          box-sizing: border-box;
        }
        .email-leads-page textarea {
          min-height: 110px;
          resize: vertical;
        }
        .email-leads-page input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: #0f172a;
        }
        .email-leads-page table th {
          font-weight: 800;
          white-space: nowrap;
        }
        .email-leads-page table td {
          vertical-align: top;
        }
        @media (max-width: 860px) {
          .email-leads-page .email-composer-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <section style={panelStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 24, color: "#0f172a" }}>
              Email Leads
            </h1>
            <p style={{ margin: "6px 0 0", color: "#475569", fontSize: 13 }}>
              Captured email addresses, audience filters, selection, and careful email sends.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            style={{
              border: "1px solid #cbd5e1",
              background: "#fff",
              color: "#0f172a",
              borderRadius: 0,
              padding: "8px 12px",
              fontWeight: 800,
            }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {newLeadCount > 0 ? (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
              border: "1px solid #bbf7d0",
              background: "#f0fdf4",
              color: "#166534",
              padding: 12,
              flexWrap: "wrap",
            }}
          >
            <strong>
              {newLeadCount} new email{newLeadCount === 1 ? "" : "s"} captured.
            </strong>
            <button
              type="button"
              onClick={markSeen}
              style={{
                border: "1px solid #16a34a",
                background: "#16a34a",
                color: "#fff",
                borderRadius: 0,
                padding: "6px 10px",
                fontWeight: 800,
              }}
            >
              Mark Seen
            </button>
          </div>
        ) : null}
      </section>

      <section style={panelStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: 18 }}>Filters</h2>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gap: 10,
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          }}
        >
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Search</span>
            <input
              placeholder="Email, code, page, source"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Status</span>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="">Any status</option>
              <option value="captured">Captured</option>
              <option value="converted">Converted</option>
              <option value="suppressed">Suppressed</option>
            </select>
          </label>
          <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Signup code</span>
            <input
              placeholder="WELCOME10"
              value={filters.couponCode}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, couponCode: e.target.value.toUpperCase() }))
              }
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Registered from</span>
            <input
              type="date"
              value={filters.registeredFrom}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, registeredFrom: e.target.value }))
              }
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Registered to</span>
            <input
              type="date"
              value={filters.registeredTo}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, registeredTo: e.target.value }))
              }
            />
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Paid orders</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <input
                type="number"
                min={0}
                placeholder="Min"
                value={filters.minOrders}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, minOrders: e.target.value }))
                }
              />
              <input
                type="number"
                min={0}
                placeholder="Max"
                value={filters.maxOrders}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, maxOrders: e.target.value }))
                }
              />
            </div>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Lifetime spend</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <input
                type="number"
                min={0}
                placeholder="Min $"
                value={filters.minSpend}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, minSpend: e.target.value }))
                }
              />
              <input
                type="number"
                min={0}
                placeholder="Max $"
                value={filters.maxSpend}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, maxSpend: e.target.value }))
                }
              />
            </div>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Conversion</span>
            <select
              value={filters.converted}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, converted: e.target.value }))
              }
            >
              <option value="">Any</option>
              <option value="true">Converted</option>
              <option value="false">Not converted</option>
            </select>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Signup email</span>
            <select
              value={filters.emailSent}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, emailSent: e.target.value }))
              }
            >
              <option value="">Any</option>
              <option value="sent">Sent</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label style={labelStyle}>
            <span style={{ fontSize: 12, color: "#475569" }}>Items to show</span>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value || 25))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
      </section>

      <section style={panelStyle}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            border: "1px solid #e2e8f0",
            borderBottom: 0,
          }}
        >
          {[
            ["Leads", data?.summary.totalCaptures || 0],
            ["Converted", data?.summary.convertedCaptures || 0],
            ["Email pending", data?.summary.unsentCaptures || 0],
            ["Paid orders", data?.summary.totalPaidOrders || 0],
            ["Lifetime spend", money(data?.summary.totalLifetimeSpend || 0)],
          ].map(([label, value]) => (
            <div
              key={String(label)}
              style={{
                padding: 12,
                borderRight: "1px solid #e2e8f0",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>
                {label}
              </div>
              <div style={{ marginTop: 4, fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, overflowX: "auto", border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", minWidth: 980, borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", color: "#334155" }}>
                <th style={{ padding: 8, textAlign: "left" }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleRows}
                    aria-label="Select all visible email leads"
                  />
                </th>
                <th style={{ padding: 8, textAlign: "left" }}>Email</th>
                <th style={{ padding: 8, textAlign: "left" }}>Registered</th>
                <th style={{ padding: 8, textAlign: "left" }}>Orders</th>
                <th style={{ padding: 8, textAlign: "left" }}>Spend</th>
                <th style={{ padding: 8, textAlign: "left" }}>Signup Email</th>
                <th style={{ padding: 8, textAlign: "left" }}>Campaign</th>
                <th style={{ padding: 8, textAlign: "left" }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((item) => (
                <tr key={item._id} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={{ padding: 8 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(selectedIds[item._id])}
                      onChange={(e) =>
                        setSelectedIds((prev) => ({
                          ...prev,
                          [item._id]: e.target.checked,
                        }))
                      }
                      aria-label={`Select ${item.email}`}
                    />
                  </td>
                  <td style={{ padding: 8 }}>
                    <div style={{ fontWeight: 900, color: "#0f172a" }}>{item.email}</div>
                    <div style={{ color: "#64748b", fontSize: 12 }}>{item.status}</div>
                  </td>
                  <td style={{ padding: 8 }}>{formatDateTime(item.createdAt)}</td>
                  <td style={{ padding: 8 }}>{item.paidOrderCount || 0}</td>
                  <td style={{ padding: 8 }}>{money(item.lifetimeSpend)}</td>
                  <td style={{ padding: 8 }}>
                    <div>{item.couponCode || "-"}</div>
                    <div style={{ color: item.emailSendError ? "#b91c1c" : "#64748b", fontSize: 12 }}>
                      {item.emailSendError
                        ? "Email failed"
                        : item.emailSentAt
                          ? `Sent ${formatDateTime(item.emailSentAt)}`
                          : "Not sent"}
                    </div>
                  </td>
                  <td style={{ padding: 8 }}>
                    {item.lastCampaignEmailSentAt ? (
                      <>
                        <div>{formatDateTime(item.lastCampaignEmailSentAt)}</div>
                        <div style={{ color: "#64748b", fontSize: 12 }}>
                          {item.lastCampaignEmailSubject || "Campaign email"}
                        </div>
                      </>
                    ) : item.lastCampaignEmailError ? (
                      <span style={{ color: "#b91c1c" }}>Failed</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ padding: 8 }}>
                    <div>{item.source || "-"}</div>
                    <div style={{ maxWidth: 240, color: "#64748b", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.path || "-"}
                    </div>
                  </td>
                </tr>
              ))}
              {!visibleRows.length ? (
                <tr>
                  <td colSpan={8} style={{ padding: 14, color: "#64748b" }}>
                    {loading ? "Loading email leads..." : "No email leads match these filters."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section style={panelStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: 18 }}>Send Email</h2>
        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 13 }}>
          Choose a template, apply it, edit the placeholders, then send to selected leads or current filters.
        </p>
        <div
          className="email-composer-grid"
          style={{
            marginTop: 12,
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {EMAIL_TEMPLATES.map((template) => (
            <TemplatePreview
              key={template.key}
              template={template}
              selected={composer.template === template.key}
              onSelect={() =>
                setComposer((prev) => ({
                  ...prev,
                  template: template.key,
                }))
              }
            />
          ))}
        </div>

        <div
          style={{
            marginTop: 12,
            display: "grid",
            gap: 12,
            gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 420px)",
          }}
        >
          <div
            style={{
              display: "grid",
              gap: 10,
              alignContent: "start",
              border: "1px solid #e2e8f0",
              padding: 12,
            }}
          >
            <button
              type="button"
              onClick={applyTemplate}
              style={{
                justifySelf: "start",
                border: `1px solid ${selectedTemplate.accent}`,
                background: selectedTemplate.accent,
                color: "#fff",
                borderRadius: 0,
                padding: "8px 12px",
                fontWeight: 900,
              }}
            >
              Apply Selected Template
            </button>
            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Send target</span>
              <select
                value={composer.mode}
                onChange={(e) =>
                  setComposer((prev) => ({
                    ...prev,
                    mode: e.target.value === "filter" ? "filter" : "selected",
                  }))
                }
              >
                <option value="selected">Selected emails ({selectedCount})</option>
                <option value="filter">All matching current filters ({data?.total || 0})</option>
              </select>
              <span style={hintStyle}>
                Filter sends are capped at 500 recipients per send to protect deliverability.
              </span>
            </label>
            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Subject</span>
              <input
                value={composer.subject}
                placeholder={selectedTemplate.subject}
                onChange={(e) =>
                  setComposer((prev) => ({ ...prev, subject: e.target.value }))
                }
              />
            </label>
            <label style={labelStyle}>
              <span style={{ fontSize: 12, color: "#475569" }}>Message</span>
              <textarea
                value={composer.message}
                placeholder={selectedTemplate.message}
                onChange={(e) =>
                  setComposer((prev) => ({ ...prev, message: e.target.value }))
                }
                style={{ minHeight: 150, borderRadius: 0 }}
              />
              <span style={hintStyle}>
                Write like a real update. Avoid excessive punctuation, misleading discounts, or spammy wording.
              </span>
            </label>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setSelectedIds({})}
                disabled={selectedCount <= 0 || sending}
                style={{
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  color: "#0f172a",
                  borderRadius: 0,
                  padding: "8px 12px",
                  fontWeight: 800,
                }}
              >
                Clear Selection
              </button>
              <button
                type="button"
                onClick={() => void sendCampaign()}
                disabled={sending}
                style={{
                  border: "1px solid #0f172a",
                  background: "#0f172a",
                  color: "#fff",
                  borderRadius: 0,
                  padding: "8px 12px",
                  fontWeight: 900,
                }}
              >
                {sending ? "Sending..." : "Send Email"}
              </button>
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              background: "#fff",
              alignSelf: "start",
            }}
          >
            <div style={{ background: selectedTemplate.soft, padding: 18, borderBottom: "1px solid #e2e8f0" }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: selectedTemplate.accent,
                }}
              >
                Beyond Pendants
              </div>
              <div style={{ marginTop: 8, fontSize: 22, lineHeight: 1.2, fontWeight: 900, color: "#0f172a" }}>
                {composer.subject || selectedTemplate.subject}
              </div>
            </div>
            <div style={{ padding: 18, color: "#334155", fontSize: 14, lineHeight: 1.65 }}>
              {(composer.message || selectedTemplate.message)
                .split(/\n{2,}/)
                .map((part, index) => (
                  <p key={`${part}-${index}`} style={{ margin: index === 0 ? 0 : "12px 0 0" }}>
                    {part}
                  </p>
                ))}
              <span
                style={{
                  display: "inline-block",
                  marginTop: 18,
                  background: selectedTemplate.accent,
                  color: "#fff",
                  padding: "10px 12px",
                  fontWeight: 900,
                }}
              >
                {selectedTemplate.cta}
              </span>
            </div>
            <div style={{ borderTop: "1px solid #e2e8f0", padding: 14, color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
              You are receiving this because you shared your email with Beyond Pendants for product updates or discount offers. Reply to this email for help.
            </div>
          </div>
        </div>

        {notice ? <p style={{ color: "#166534", fontSize: 13 }}>{notice}</p> : null}
        {error ? <p style={{ color: "#b91c1c", fontSize: 13 }}>{error}</p> : null}
      </section>
    </div>
  );
}
